import { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api, { apiError, photoUrl } from "../lib/api";
import { toast } from "sonner";
import jsPDF from "jspdf";
import SignatureCanvas from "react-signature-canvas";
import { useAuth } from "../context/AuthContext";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import { ArrowLeft, Upload, Trash2, Download, FileText, Signature, Camera, Activity, ClipboardList, Receipt, Plus, Link2, Copy, Send, Mail, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Share2, Archive, FolderOpen, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function PatientDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "es" ? "es-ES" : "it-IT";
  const [patient, setPatient] = useState(null);
  const [tab, setTab] = useState(searchParams.get("tab") || "info");

  const TABS = [
    { key: "info", label: t("patientDetail.tabInfo"), icon: ClipboardList },
    { key: "evolucion", label: t("patientDetail.tabEvolution"), icon: Activity },
    { key: "controles", label: t("patientDetail.tabControls"), icon: ClipboardList },
    { key: "fotos", label: t("patientDetail.tabPhotos"), icon: Camera },
    { key: "documentos", label: t("patientDetail.tabDocuments"), icon: FolderOpen },
    { key: "consentimiento", label: t("patientDetail.tabConsent"), icon: Signature },
    { key: "facturas", label: t("patientDetail.tabInvoices"), icon: Receipt },
  ];

  useEffect(() => { api.get(`/patients/${id}`).then((r) => setPatient(r.data)).catch(() => toast.error(t("patientDetail.loadError"))); }, [id, t]);
  if (!patient) return <div className="text-stone-500">{t("common.loading")}</div>;

  return (
    <div className="space-y-8 fade-in-up">
      <div>
        <Link to="/pacientes" className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-stone-900 inline-flex items-center gap-1 mb-4" data-testid="back-to-patients">
          <ArrowLeft className="w-3 h-3" /> {t("nav.patients")}
        </Link>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-2">{t("patientDetail.eyebrow")}</p>
            <h1 className="font-heading text-5xl font-light" data-testid="patient-name">{patient.full_name}</h1>
            <p className="text-sm text-stone-500 mt-2">{patient.phone} · {patient.email || t("patientDetail.noEmail")}</p>
          </div>
        </div>
      </div>

      <div className="border-b border-stone-200 flex gap-1 overflow-x-auto">
        {TABS.map((tt) => (
          <button key={tt.key} onClick={() => setTab(tt.key)} data-testid={`tab-${tt.key}`}
            className={`flex items-center gap-2 px-5 py-3 text-sm border-b-2 -mb-px transition-all whitespace-nowrap ${
              tab === tt.key ? "border-[#D4A373] text-stone-900" : "border-transparent text-stone-500 hover:text-stone-900"
            }`}>
            <tt.icon className="w-4 h-4" strokeWidth={1.5} /> {tt.label}
          </button>
        ))}
      </div>

      {tab === "info" && <InfoTab patient={patient} onUpdate={setPatient} />}
      {tab === "fotos" && <PhotosTab pid={id} />}
      {tab === "documentos" && <DocumentsTab pid={id} patient={patient} />}
      {tab === "evolucion" && <EvolutionTab pid={id} patient={patient} />}
      {tab === "controles" && <ControlsTab pid={id} patient={patient} />}
      {tab === "consentimiento" && <ConsentTab pid={id} patient={patient} />}
      {tab === "facturas" && <InvoicesTab pid={id} patient={patient} locale={locale} />}
    </div>
  );
}

function InfoTab({ patient, onUpdate }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(patient);
  const [editing, setEditing] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  const [loadingInvite, setLoadingInvite] = useState(false);
  async function save() {
    try {
      const { data } = await api.put(`/patients/${patient.id}`, form);
      onUpdate(data); setEditing(false); toast.success(t("common.saved"));
    } catch (e) { toast.error(apiError(e)); }
  }
  async function generateInvite() {
    setLoadingInvite(true);
    try {
      const { data } = await api.post(`/patients/${patient.id}/invite`);
      setInviteUrl(`${window.location.origin}${data.claim_url}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoadingInvite(false); }
  }
  function copyInvite() { navigator.clipboard.writeText(inviteUrl); toast.success(t("common.linkCopied") || "Link copiato"); }
  function shareWhatsApp() {
    const phone = (patient.phone || "").replace(/[^\d+]/g, "");
    const msg = encodeURIComponent(`${t("patientInvite.waMsg") || "Ciao"} ${patient.full_name || ""}, attiva il tuo profilo Ambra qui: ${inviteUrl}`);
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  }
  function shareEmail() {
    const subject = encodeURIComponent(t("patientInvite.emailSubject") || "Il tuo profilo Ambra");
    const body = encodeURIComponent(`${t("patientInvite.waMsg") || "Ciao"} ${patient.full_name || ""},\n\nAttiva il tuo profilo per accedere ai tuoi documenti e prenotare le visite:\n${inviteUrl}\n\n`);
    window.open(`mailto:${patient.email || ""}?subject=${subject}&body=${body}`, "_blank");
  }
  const F = (k, label) => (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{label}</label>
      {editing ? (
        <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`edit-${k}`}
          className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#D4A373]" />
      ) : (
        <p className="text-stone-900">{patient[k] || "—"}</p>
      )}
    </div>
  );
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-8 space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        {F("full_name", t("patients.fullName"))}
        {F("phone", t("common.phone"))}
        {F("email", t("common.email"))}
        {F("date_of_birth", t("patients.dob"))}
        {F("codice_fiscale", t("patients.cf"))}
        {F("allergies", t("patients.allergies"))}
        {F("diseases", t("patients.diseases"))}
        {F("treatments_interest", t("patients.treatmentInterest"))}
      </div>
      <div>
        <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.notes")}</label>
        {editing ? (
          <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4}
            spellCheck={true} autoCorrect="on" autoCapitalize="sentences"
            className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
        ) : (
          <p className="text-stone-700 whitespace-pre-wrap">{patient.notes || "—"}</p>
        )}
      </div>
      <div className="flex gap-3 pt-4 border-t border-stone-100">
        {editing ? (
          <>
            <button onClick={save} data-testid="save-patient-info" className="px-6 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800">{t("common.save")}</button>
            <button onClick={() => { setEditing(false); setForm(patient); }} className="px-6 py-2.5 rounded-full border border-stone-200">{t("common.cancel")}</button>
          </>
        ) : (
          <button onClick={() => setEditing(true)} data-testid="edit-patient-info" className="px-6 py-2.5 rounded-full border border-stone-200 hover:bg-stone-50">{t("common.edit")}</button>
        )}
      </div>

      {/* Invite link — only if patient hasn't claimed their portal yet */}
      {!patient.patient_user_id && !editing && (
        <div className="bg-[#F5EFE6] border border-[#D4A373]/40 rounded-2xl p-5 space-y-3" data-testid="invite-card">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0">
              <Send className="w-4 h-4 text-[#B07D4F]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-lg font-light">{t("patientInvite.title") || "Invita al portale"}</div>
              <p className="text-xs text-stone-500 mt-0.5">{t("patientInvite.hint") || "Manda un link al paziente perché crei la sua password e acceda al suo profilo."}</p>
            </div>
          </div>
          {!inviteUrl ? (
            <button onClick={generateInvite} disabled={loadingInvite} data-testid="generate-invite"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-stone-700 disabled:opacity-50">
              <Link2 className="w-3.5 h-3.5" /> {loadingInvite ? "…" : (t("patientInvite.generate") || "Genera link invito")}
            </button>
          ) : (
            <>
              <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-xl px-3 py-2">
                <Link2 className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" />
                <input readOnly value={inviteUrl} data-testid="invite-url"
                  className="flex-1 bg-transparent text-xs outline-none min-w-0 truncate" />
                <button onClick={copyInvite} data-testid="copy-invite" className="text-stone-500 hover:text-stone-900 flex-shrink-0"><Copy className="w-3.5 h-3.5" /></button>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={shareWhatsApp} disabled={!patient.phone} data-testid="whatsapp-invite"
                  className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-[#8FA08B] text-white text-xs hover:opacity-90 disabled:opacity-30">
                  <Send className="w-3 h-3" /> WhatsApp
                </button>
                <button onClick={shareEmail} disabled={!patient.email} data-testid="email-invite"
                  className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs hover:bg-stone-700 disabled:opacity-30">
                  <Mail className="w-3 h-3" /> Email
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PhotosTab({ pid }) {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const galleryRef = useRef();
  const cameraRef = useRef();
  const [kind, setKind] = useState("before");
  const [label, setLabel] = useState("");
  const [pair, setPair] = useState(0);

  const load = async () => { const { data } = await api.get(`/patients/${pid}/photos`); setPhotos(data); };
  useEffect(() => { load(); }, [pid]);

  async function onUpload(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const file of files) {
      try {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/patients/${pid}/photos?kind=${kind}&label=${encodeURIComponent(label)}`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (err) { fail++; }
    }
    toast.success(`${ok} / ${ok + fail}`);
    setLabel(""); await load();
    setUploading(false); e.target.value = "";
  }
  async function del(id) {
    if (!window.confirm(t("patientDetail.confirmDeletePhoto"))) return;
    await api.delete(`/photos/${id}`); toast.success(t("patientDetail.photoDeleted")); load();
  }
  async function reorder(id, direction) {
    await api.patch(`/photos/${id}/reorder?direction=${direction}`);
    load();
  }
  async function downloadOne(p) {
    try {
      const res = await fetch(photoUrl(p.id));
      const blob = await res.blob();
      const ext = (p.content_type || "image/jpeg").split("/")[1] || "jpg";
      const kindLbl = p.kind === "before" ? "prima" : "dopo";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${kindLbl}-${String(p.position || 0).padStart(2, "0")}.${ext === "jpeg" ? "jpg" : ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch { toast.error(t("common.error") || "Error"); }
  }
  async function shareOne(p) {
    try {
      const res = await fetch(photoUrl(p.id));
      const blob = await res.blob();
      const ext = (p.content_type || "image/jpeg").split("/")[1] || "jpg";
      const kindLbl = p.kind === "before" ? "prima" : "dopo";
      const filename = `${kindLbl}-${String(p.position || 0).padStart(2, "0")}.${ext === "jpeg" ? "jpg" : ext}`;
      const file = new File([blob], filename, { type: blob.type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: filename });
      } else {
        await navigator.clipboard.writeText(photoUrl(p.id));
        toast.success(t("patientDetail.photoShareLinkCopied"));
      }
    } catch { toast.error(t("patientDetail.photoShareNotSupported")); }
  }
  function downloadAllZip() {
    const token = localStorage.getItem("token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/patients/${pid}/photos/zip?auth=${token}`, "_blank");
  }

  const before = photos.filter((p) => p.kind === "before");
  const after = photos.filter((p) => p.kind === "after");
  const maxPairs = Math.max(before.length, after.length);
  const bPair = before[pair];
  const aPair = after[pair];

  return (
    <div className="space-y-8">
      <div className="bg-white border border-stone-200 rounded-2xl p-6 flex flex-wrap items-end gap-4">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("patientDetail.photoType")}</label>
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="photo-kind"
            className="px-4 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-xl">
            <option value="before">{t("patientDetail.photoTypeBefore")}</option>
            <option value="after">{t("patientDetail.photoTypeAfter")}</option>
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("patientDetail.photoLabelOpt")}</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="photo-label"
            className="w-full px-4 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
        </div>
        <input ref={galleryRef} type="file" accept="image/*" multiple onChange={onUpload} className="hidden" />
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onUpload} className="hidden" />
        <button onClick={() => cameraRef.current.click()} disabled={uploading} data-testid="take-photo-btn"
          className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800 transition-colors">
          <Camera className="w-4 h-4" /> {uploading ? t("patientDetail.uploading") : t("patientDetail.takePhoto")}
        </button>
        <button onClick={() => galleryRef.current.click()} disabled={uploading} data-testid="upload-photo-btn"
          className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white transition-colors">
          <Upload className="w-4 h-4" /> {uploading ? t("patientDetail.uploading") : t("patientDetail.uploadPhoto")}
        </button>
        {photos.length > 0 && (
          <button onClick={downloadAllZip} data-testid="download-zip-btn"
            className="flex items-center gap-2 px-6 py-2.5 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-50 transition-colors">
            <Archive className="w-4 h-4" /> {t("patientDetail.photoDownloadAllZip")}
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <PhotoSection title={t("patientDetail.photoTypeBefore")} photos={before} onDelete={del}
          onReorder={reorder} onDownload={downloadOne} onShare={shareOne}
          total={before.length} noPhotosLbl={t("patientDetail.noPhotos")} tLabels={t} />
        <PhotoSection title={t("patientDetail.photoTypeAfter")} photos={after} onDelete={del}
          onReorder={reorder} onDownload={downloadOne} onShare={shareOne}
          total={after.length} noPhotosLbl={t("patientDetail.noPhotos")} tLabels={t} />
      </div>

      {maxPairs > 0 && (
        <div className="bg-white border border-stone-200 rounded-2xl p-6">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
            <div>
              <h3 className="font-heading text-2xl font-light">{t("patientDetail.compareTitle")}</h3>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mt-1">{t("patientDetail.compareHint")}</p>
            </div>
            {maxPairs > 1 && (
              <div className="flex items-center gap-2">
                <button onClick={() => setPair(Math.max(0, pair - 1))} disabled={pair === 0}
                  data-testid="compare-prev"
                  className="p-2 rounded-full border border-stone-200 hover:bg-stone-50 disabled:opacity-30">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-stone-600 min-w-[100px] text-center" data-testid="compare-index">
                  {t("patientDetail.comparePair", { n: pair + 1, total: maxPairs })}
                </span>
                <button onClick={() => setPair(Math.min(maxPairs - 1, pair + 1))} disabled={pair >= maxPairs - 1}
                  data-testid="compare-next"
                  className="p-2 rounded-full border border-stone-200 hover:bg-stone-50 disabled:opacity-30">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          {bPair && aPair ? (
            <BeforeAfterSlider
              beforeSrc={photoUrl(bPair.id)}
              afterSrc={photoUrl(aPair.id)}
              beforeLabel={t("patientDetail.photoTypeBefore")}
              afterLabel={t("patientDetail.photoTypeAfter")}
            />
          ) : (
            <div className="bg-[#FAF9F6] border border-dashed border-stone-300 rounded-xl p-8 text-center text-stone-500">
              {!bPair
                ? t("patientDetail.compareMissingBefore", { n: pair + 1 })
                : t("patientDetail.compareMissingAfter", { n: pair + 1 })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PhotoSection({ title, photos, onDelete, onReorder, onDownload, onShare, total, noPhotosLbl, tLabels }) {
  return (
    <div>
      <h3 className="font-heading text-2xl font-light mb-4">{title} <span className="text-stone-400 text-sm">({total})</span></h3>
      {photos.length === 0 ? (
        <div className="border border-dashed border-stone-200 rounded-2xl h-48 flex items-center justify-center text-stone-400 text-sm">
          {noPhotosLbl}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {photos.map((p, idx) => (
            <div key={p.id} data-testid={`photo-${p.id}`} className="relative group aspect-square bg-stone-100 rounded-xl overflow-hidden">
              <img src={photoUrl(p.id)} alt={p.label || ""} className="w-full h-full object-cover" />
              <span className="absolute top-2 left-2 text-[10px] font-medium bg-white/95 text-stone-900 px-2 py-0.5 rounded-full shadow-sm">
                {p.position || idx + 1}
              </span>
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                <div className="flex gap-1.5">
                  <button onClick={() => onReorder(p.id, "up")} disabled={idx === 0} title={tLabels("patientDetail.photoMoveUp")}
                    data-testid={`photo-up-${p.id}`}
                    className="p-1.5 rounded-full bg-white/90 hover:bg-white text-stone-900 disabled:opacity-30">
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => onReorder(p.id, "down")} disabled={idx === photos.length - 1} title={tLabels("patientDetail.photoMoveDown")}
                    data-testid={`photo-down-${p.id}`}
                    className="p-1.5 rounded-full bg-white/90 hover:bg-white text-stone-900 disabled:opacity-30">
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => onDownload(p)} title={tLabels("patientDetail.photoDownload")}
                    data-testid={`photo-dl-${p.id}`}
                    className="p-1.5 rounded-full bg-white/90 hover:bg-white text-stone-900">
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => onShare(p)} title={tLabels("patientDetail.photoShare")}
                    data-testid={`photo-share-${p.id}`}
                    className="p-1.5 rounded-full bg-white/90 hover:bg-white text-stone-900">
                    <Share2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => onDelete(p.id)} data-testid={`photo-del-${p.id}`}
                    className="p-1.5 rounded-full bg-red-500/90 hover:bg-red-500 text-white">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {p.label && <span className="absolute bottom-2 left-2 text-xs bg-white/90 px-2 py-1 rounded">{p.label}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const DOC_CATEGORIES = ["analisi", "istologia", "ecografia", "referto", "preop", "altro"];

function DocumentsTab({ pid, patient }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "es" ? "es-ES" : "it-IT";
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("analisi");
  const [notes, setNotes] = useState("");
  const inputRef = useRef();

  const load = async () => { const { data } = await api.get(`/patients/${pid}/documents`); setDocs(data); };
  useEffect(() => { load(); }, [pid]);

  async function onUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const file of files) {
      try {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/patients/${pid}/documents?category=${category}&notes=${encodeURIComponent(notes)}`,
          fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch { fail++; }
    }
    toast.success(`${ok} / ${ok + fail}`);
    setNotes(""); await load();
    setUploading(false); e.target.value = "";
  }

  async function del(id) {
    if (!window.confirm(t("patientDetail.docConfirmDelete"))) return;
    await api.delete(`/documents/${id}`);
    toast.success(t("patientDetail.docDeleted")); load();
  }

  function docUrl(id) {
    const token = localStorage.getItem("token");
    return `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/download?auth=${token}`;
  }

  function shareWa(d) {
    const raw = (patient.phone || "").trim();
    if (!raw) { toast.error(t("patientDetail.missingPhone")); return; }
    const phone = raw.replace(/[\s-]/g, "").replace("+", "");
    const url = docUrl(d.id);
    const msg = `${patient.full_name} — ${d.original_filename}: ${url}`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  }
  function shareEmail(d) {
    const to = (patient.email || "").trim();
    if (!to) { toast.error(t("patientDetail.missingEmail")); return; }
    const url = docUrl(d.id);
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(d.original_filename)}&body=${encodeURIComponent(url)}`;
  }

  function categoryLabel(k) {
    const map = { analisi: "docCategoryAnalisi", istologia: "docCategoryIstologia",
      ecografia: "docCategoryEcografia", referto: "docCategoryReferto",
      preop: "docCategoryPreOp", altro: "docCategoryAltro" };
    return t(`patientDetail.${map[k] || "docCategoryAltro"}`);
  }
  function humanSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  return (
    <div className="space-y-8">
      <div className="bg-white border border-stone-200 rounded-2xl p-6 flex flex-wrap items-end gap-4">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("patientDetail.docCategoryLabel")}</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="doc-category"
            className="px-4 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-xl">
            {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("patientDetail.docNotesOpt")}</label>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="doc-notes"
            className="w-full px-4 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
        </div>
        <input ref={inputRef} type="file" accept="application/pdf,image/*" multiple onChange={onUpload} className="hidden" />
        <button onClick={() => inputRef.current.click()} disabled={uploading} data-testid="doc-upload-btn"
          className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white transition-colors">
          <Upload className="w-4 h-4" /> {uploading ? t("patientDetail.docUploading") : t("patientDetail.docUpload")}
        </button>
      </div>

      <div>
        {docs.length === 0 ? (
          <p className="text-stone-500 text-sm">{t("patientDetail.docNoFiles")}</p>
        ) : (
          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
            <ul className="divide-y divide-stone-100">
              {docs.map((d) => (
                <li key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center gap-4 px-5 py-4 hover:bg-stone-50 transition-colors">
                  <FileText className="w-5 h-5 text-[#D4A373] flex-shrink-0" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-stone-900 truncate">{d.original_filename}</div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-stone-500 flex-wrap">
                      <span className="inline-block px-2 py-0.5 rounded-full bg-[#FAF9F6] border border-stone-200 uppercase tracking-widest text-[10px]">
                        {categoryLabel(d.category)}
                      </span>
                      {d.uploaded_by === "patient" && (
                        <span data-testid={`doc-from-patient-${d.id}`}
                          className="inline-block px-2 py-0.5 rounded-full bg-[#8FA08B]/20 text-[#5a6d54] uppercase tracking-widest text-[10px]">
                          {t("patientDetail.docFromPatient")}
                        </span>
                      )}
                      <span>{new Date(d.created_at).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" })}</span>
                      {d.size_bytes ? <span>· {humanSize(d.size_bytes)}</span> : null}
                      {d.notes ? <span className="italic">· {d.notes}</span> : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <a href={docUrl(d.id)} target="_blank" rel="noreferrer" data-testid={`doc-open-${d.id}`}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-stone-200 hover:bg-stone-100 text-stone-700">
                      <ExternalLink className="w-3 h-3" /> {t("patientDetail.docViewOpen")}
                    </a>
                    <a href={docUrl(d.id)} download={d.original_filename} data-testid={`doc-dl-${d.id}`}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white">
                      <Download className="w-3 h-3" /> PDF
                    </a>
                    <button onClick={() => shareWa(d)} data-testid={`doc-wa-${d.id}`}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[#8FA08B] text-white hover:opacity-90">
                      <Send className="w-3 h-3" /> WhatsApp
                    </button>
                    <button onClick={() => shareEmail(d)} data-testid={`doc-email-${d.id}`}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-stone-700">
                      <Mail className="w-3 h-3" /> Email
                    </button>
                    <button onClick={() => del(d.id)} data-testid={`doc-del-${d.id}`}
                      className="text-stone-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}


const ANAMNESIS_TEMPLATE = `• Allergie: 
• Patologie mediche: 
• Interventi chirurgici: 
• Terapia in atto: `;

// Fetch the doctor's clinic logo as a data URL for embedding in jsPDF.
// Returns { dataUrl, width, height } or null if no logo / load failure.
async function loadLogoDataUrl(user) {
  if (!user?.logo_url) return null;
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}${user.logo_url}`;
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
    const dims = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => resolve({ w: 0, h: 0 });
      img.src = dataUrl;
    });
    if (!dims.w || !dims.h) return null;
    return { dataUrl, width: dims.w, height: dims.h };
  } catch {
    return null;
  }
}

// Draw the logo centered at the top of the current PDF page and return the new y position.
function drawTopLogo(doc, logo, startY = 12) {
  if (!logo) return startY;
  const pageW = doc.internal.pageSize.getWidth();
  const maxH = 22; // mm
  const maxW = 60; // mm
  const ratio = logo.width / logo.height;
  let h = maxH;
  let w = h * ratio;
  if (w > maxW) { w = maxW; h = w / ratio; }
  const x = (pageW - w) / 2;
  const fmt = logo.dataUrl.includes("image/png") ? "PNG"
             : logo.dataUrl.includes("image/webp") ? "WEBP" : "JPEG";
  try { doc.addImage(logo.dataUrl, fmt, x, startY, w, h); }
  catch { return startY; }
  return startY + h + 4;
}

function EvolutionTab({ pid, patient }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const locale = i18n.language === "es" ? "es-ES" : "it-IT";
  const [items, setItems] = useState([]);
  const [text, setText] = useState(ANAMNESIS_TEMPLATE);
  const [date, setDate] = useState("");
  const load = async () => { const { data } = await api.get(`/patients/${pid}/evolutions`); setItems(data); };
  useEffect(() => { load(); }, [pid]);
  async function add() {
    if (!text.trim() || text.trim() === ANAMNESIS_TEMPLATE.trim()) return;
    await api.post(`/patients/${pid}/evolutions`, { text, date: date ? new Date(date).toISOString() : undefined });
    setText(ANAMNESIS_TEMPLATE); setDate(""); toast.success(t("patientDetail.anamnesisAdded")); load();
  }
  async function del(id) { await api.delete(`/evolutions/${id}`); load(); }

  function downloadPDF() {
    if (items.length === 0) { toast.error(t("patientDetail.noEntries")); return; }
    (async () => {
      const logo = await loadLogoDataUrl(user);
      const doc = new jsPDF();
      const pageW = doc.internal.pageSize.getWidth();
      const marginX = 20;
      let y = drawTopLogo(doc, logo, 12);
      if (y < 25) y = 25;
      doc.setFontSize(18); doc.text(t("patientDetail.anamnesisPdfTitle"), pageW / 2, y, { align: "center" });
      y += 10;
      doc.setFontSize(11);
      doc.text(`${t("patients.fullName")}: ${patient.full_name}`, marginX, y); y += 6;
      if (patient.date_of_birth) { doc.text(`${t("patients.dob")}: ${patient.date_of_birth}`, marginX, y); y += 6; }
      if (patient.codice_fiscale) { doc.text(`${t("patients.cf")}: ${patient.codice_fiscale}`, marginX, y); y += 6; }
      doc.text(`${t("patientDetail.pdfExportedOn")}: ${new Date().toLocaleString(locale, { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}`, marginX, y);
      y += 4;
      doc.setDrawColor(212, 163, 115); doc.setLineWidth(0.4); doc.line(marginX, y, pageW - marginX, y);
      y += 8;

      const sorted = [...items].sort((a, b) => new Date(a.date) - new Date(b.date));
      sorted.forEach((it, idx) => {
        const dt = new Date(it.date);
        const dateStr = dt.toLocaleString(locale, { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
        const lines = doc.splitTextToSize(it.text || "", pageW - marginX * 2);
        const needed = 12 + lines.length * 5;
        if (y + needed > 280) { doc.addPage(); y = 25; }
        doc.setFontSize(10); doc.setTextColor(120, 113, 108);
        doc.text(`${idx + 1}. ${dateStr}`, marginX, y);
        y += 6;
        doc.setFontSize(11); doc.setTextColor(28, 25, 23);
        doc.text(lines, marginX, y);
        y += lines.length * 5 + 6;
      });

      const safeName = (patient.full_name || "paciente").replace(/\s+/g, "_");
      drawDoctorFooter(doc, user, y);
      doc.save(`anamnesis-${safeName}.pdf`);
    })();
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border border-stone-200 rounded-2xl p-6 space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.date")} & {t("common.time") || "ora"}</label>
            <input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} data-testid="evolution-date"
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
            <p className="text-[10px] text-stone-400 mt-1">{t("patientDetail.dateOptionalHint") || "Se lo lasci vuoto verrà usato il momento attuale."}</p>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("patientDetail.newAnamnesisNote")}</label>
          <p className="text-xs text-stone-500 mb-3">{t("patientDetail.anamnesisHint")}</p>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} data-testid="evolution-input"
            spellCheck={true} autoCorrect="on" autoCapitalize="sentences" lang="it"
            className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#D4A373]" />
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={add} data-testid="add-evolution" className="px-6 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800">
            <Plus className="w-4 h-4 inline mr-1" /> {t("patientDetail.saveAnamnesis")}
          </button>
          <button onClick={() => setText(ANAMNESIS_TEMPLATE)} data-testid="reset-anamnesis"
            className="px-6 py-2.5 rounded-full border border-stone-200 text-stone-600 hover:bg-stone-50">
            {t("patientDetail.resetTemplate")}
          </button>
          <button onClick={downloadPDF} disabled={items.length === 0} data-testid="anamnesis-download-pdf"
            className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-[#D4A373] disabled:hover:text-stone-900">
            <Download className="w-4 h-4" /> {t("patientDetail.downloadAnamnesisPdf")}
          </button>
        </div>
      </div>
      <Timeline items={items} onDelete={del} testPrefix="evolution" noEntriesLbl={t("patientDetail.noEntries")} />
    </div>
  );
}

function ControlsTab({ pid, patient }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const locale = i18n.language === "es" ? "es-ES" : "it-IT";
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [date, setDate] = useState("");
  const load = async () => { const { data } = await api.get(`/patients/${pid}/controls`); setItems(data); };
  useEffect(() => { load(); }, [pid]);
  async function add() {
    if (!text.trim()) return;
    await api.post(`/patients/${pid}/controls`, { text, date: date ? new Date(date).toISOString() : undefined });
    setText(""); setDate(""); toast.success(t("patientDetail.controlAdded")); load();
  }
  async function del(id) { await api.delete(`/controls/${id}`); load(); }

  function downloadPDF() {
    if (items.length === 0) { toast.error(t("patientDetail.noEntries")); return; }
    (async () => {
      const logo = await loadLogoDataUrl(user);
      const doc = new jsPDF();
      const pageW = doc.internal.pageSize.getWidth();
      const marginX = 20;
      let y = drawTopLogo(doc, logo, 12);
      if (y < 25) y = 25;
      doc.setFontSize(18); doc.text(t("patientDetail.controlsPdfTitle"), pageW / 2, y, { align: "center" });
      y += 10;
      doc.setFontSize(11);
      doc.text(`${t("patients.fullName")}: ${patient.full_name}`, marginX, y); y += 6;
      if (patient.date_of_birth) { doc.text(`${t("patients.dob")}: ${patient.date_of_birth}`, marginX, y); y += 6; }
      if (patient.codice_fiscale) { doc.text(`${t("patients.cf")}: ${patient.codice_fiscale}`, marginX, y); y += 6; }
      doc.text(`${t("patientDetail.pdfExportedOn")}: ${new Date().toLocaleString(locale, { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}`, marginX, y);
      y += 4;
      doc.setDrawColor(212, 163, 115); doc.setLineWidth(0.4); doc.line(marginX, y, pageW - marginX, y);
      y += 8;

      const sorted = [...items].sort((a, b) => new Date(a.date) - new Date(b.date));
      sorted.forEach((it, idx) => {
        const dt = new Date(it.date);
        const dateStr = dt.toLocaleString(locale, { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
        const lines = doc.splitTextToSize(it.text || "", pageW - marginX * 2);
        const needed = 12 + lines.length * 5;
        if (y + needed > 280) { doc.addPage(); y = 25; }
        doc.setFontSize(10); doc.setTextColor(120, 113, 108);
        doc.text(`${idx + 1}. ${dateStr}`, marginX, y);
        y += 6;
        doc.setFontSize(11); doc.setTextColor(28, 25, 23);
        doc.text(lines, marginX, y);
        y += lines.length * 5 + 6;
      });

      const safeName = (patient.full_name || "paciente").replace(/\s+/g, "_");
      drawDoctorFooter(doc, user, y);
      doc.save(`controles-${safeName}.pdf`);
    })();
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border border-stone-200 rounded-2xl p-6 space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.date")} & {t("common.time") || "ora"}</label>
            <input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} data-testid="control-date"
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
            <p className="text-[10px] text-stone-400 mt-1">{t("patientDetail.dateOptionalHint") || "Se lo lasci vuoto verrà usato il momento attuale."}</p>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("patientDetail.controlDescription")}</label>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid="control-input"
            spellCheck={true} autoCorrect="on" autoCapitalize="sentences" lang="it"
            placeholder={t("patientDetail.controlPlaceholder")}
            className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl focus:outline-none focus:border-[#D4A373]" />
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={add} data-testid="add-control" className="px-6 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800">
            <Plus className="w-4 h-4 inline mr-1" /> {t("patientDetail.registerControl")}
          </button>
          <button onClick={downloadPDF} disabled={items.length === 0} data-testid="controls-download-pdf"
            className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-[#D4A373] disabled:hover:text-stone-900">
            <Download className="w-4 h-4" /> {t("patientDetail.downloadControlsPdf")}
          </button>
        </div>
      </div>
      <Timeline items={items} onDelete={del} testPrefix="control" noEntriesLbl={t("patientDetail.noEntries")} />
    </div>
  );
}

function Timeline({ items, onDelete, testPrefix, noEntriesLbl }) {
  const { i18n } = useTranslation();
  const locale = i18n.language === "es" ? "es-ES" : "it-IT";
  if (items.length === 0) return <p className="text-stone-500 text-sm">{noEntriesLbl}</p>;
  return (
    <div className="relative pl-8">
      <div className="absolute left-2 top-2 bottom-2 w-px bg-stone-200" />
      <div className="space-y-6 stagger">
        {items.map((it) => (
          <div key={it.id} data-testid={`${testPrefix}-item-${it.id}`} className="relative">
            <div className="absolute -left-[26px] top-2 w-3 h-3 rounded-full bg-[#D4A373]" />
            <div className="bg-white border border-stone-200 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-[0.2em] text-stone-500">
                  {new Date(it.date).toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" })}
                </span>
                <button onClick={() => onDelete(it.id)} className="text-xs text-stone-400 hover:text-red-500">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-stone-800 whitespace-pre-wrap">{it.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const DEFAULT_CONSENT = `Yo, [nombre del paciente], mayor de edad, con DNI/NIE, declaro que he sido informado/a por el/la Dr./Dra. sobre el tratamiento indicado, sus objetivos, procedimiento, alternativas, riesgos y complicaciones potenciales.

He tenido la oportunidad de realizar todas las preguntas y de resolver mis dudas. Comprendo el alcance del procedimiento y otorgo mi CONSENTIMIENTO INFORMADO libre y voluntario para su realización.

Autorizo asimismo la toma de fotografías clínicas con fines exclusivamente médico-asistenciales y de seguimiento.`;

/** Draws doctor's signature + stamp block at the bottom of a jsPDF document.
 * Adds a new page if there isn't enough space. Uses the current owner's profile. */
function drawDoctorFooter(doc, user, currentY, opts = {}) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginX = 20;
  const NEEDED = 55; // vertical space required
  let y = currentY;
  if (y + NEEDED > pageH - 15) { doc.addPage(); y = 25; }
  else { y = Math.max(y + 20, pageH - NEEDED - 10); }

  // Stamp placeholder box (left) + signature line (right)
  const boxW = 55, boxH = 32;
  const stampX = marginX;
  const sigX = marginX + boxW + 20;
  const sigW = pageW - sigX - marginX;

  // Stamp dashed border box
  doc.setDrawColor(180, 173, 168); doc.setLineDashPattern([2, 2], 0);
  doc.setLineWidth(0.3);
  doc.rect(stampX, y, boxW, boxH);
  doc.setLineDashPattern([], 0);
  doc.setFontSize(8); doc.setTextColor(160, 155, 150);
  doc.text("TIMBRO", stampX + boxW / 2, y + boxH / 2 + 1, { align: "center" });

  // Signature line
  doc.setDrawColor(28, 25, 23); doc.setLineWidth(0.4);
  doc.line(sigX, y + boxH - 6, sigX + sigW, y + boxH - 6);
  doc.setFontSize(8); doc.setTextColor(120, 113, 108);
  doc.text(opts.signatureLabel || "Firma", sigX, y + boxH - 2);

  // Doctor identity block
  const infoY = y + boxH + 6;
  doc.setFontSize(10); doc.setTextColor(28, 25, 23);
  const docName = user?.name ? `Dr. ${user.name}` : "";
  doc.text(docName, sigX, infoY);
  const line2Parts = [];
  if (user?.specialization) line2Parts.push(user.specialization);
  if (user?.license_number) line2Parts.push(user.license_number);
  if (line2Parts.length) {
    doc.setFontSize(9); doc.setTextColor(120, 113, 108);
    doc.text(line2Parts.join(" · "), sigX, infoY + 5);
  }
  const line3 = user?.clinic_name || "";
  if (line3) {
    doc.setFontSize(8); doc.setTextColor(160, 155, 150);
    doc.text(line3, sigX, infoY + 10);
  }
}

function ConsentTab({ pid, patient }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "es" ? "es-ES" : "it-IT";
  const [consents, setConsents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tplRequests, setTplRequests] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [tplNotes, setTplNotes] = useState("");
  const [treatment, setTreatment] = useState("");
  const [body, setBody] = useState(DEFAULT_CONSENT);
  const [remoteLink, setRemoteLink] = useState("");
  const sigRef = useRef();

  const load = async () => {
    const [c, tp, rq] = await Promise.all([
      api.get(`/patients/${pid}/consents`),
      api.get(`/consent-templates`),
      api.get(`/patients/${pid}/consent-template-requests`),
    ]);
    setConsents(c.data); setTemplates(tp.data); setTplRequests(rq.data);
  };
  useEffect(() => { load(); }, [pid]);

  function clearSig() { sigRef.current.clear(); }

  function openWhatsAppTo(msg) {
    const raw = (patient.phone || "").trim();
    if (!raw) {
      toast.error(t("patientDetail.missingPhone"));
      return false;
    }
    const phone = raw.replace(/[\s-]/g, "").replace("+", "");
    toast.success(t("patientDetail.sendingWaTo", { phone: patient.phone }));
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
    return true;
  }

  function openEmailTo(subject, bodyMsg) {
    const to = (patient.email || "").trim();
    if (!to) {
      toast.error(t("patientDetail.missingEmail"));
      return false;
    }
    toast.success(t("patientDetail.sendingEmailTo", { email: to }));
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyMsg)}`;
    return true;
  }

  async function sendTemplate(channel = "whatsapp") {
    if (!selectedTpl) { toast.error(t("patientDetail.selectTemplate")); return; }
    // Pre-validate contact before creating the request so we don't spam the DB
    if (channel === "email" && !patient.email) { toast.error(t("patientDetail.missingEmail")); return; }
    if (channel === "whatsapp" && !patient.phone) { toast.error(t("patientDetail.missingPhone")); return; }
    try {
      const { data } = await api.post(`/patients/${pid}/consent-template-requests`, { template_id: selectedTpl, notes: tplNotes });
      const url = `${window.location.origin}${data.url_path}`;
      const tpl = templates.find((x) => x.id === selectedTpl);
      const treatmentName = tpl?.treatment || t("patientDetail.tabConsent");
      await navigator.clipboard.writeText(url).catch(() => {});
      if (channel === "email") {
        openEmailTo(`${t("patientDetail.consentEmailSubject")} — ${treatmentName}`, `${t("patientDetail.consentEmailBody")}\n\n${url}`);
      } else {
        openWhatsAppTo(`${patient.full_name} — *${treatmentName}*: ${url}`);
      }
      setSelectedTpl(""); setTplNotes(""); load();
    } catch (e) { toast.error(apiError(e)); }
  }
  function copyTplLink(rq) {
    const url = `${window.location.origin}/consentimiento-pdf/${rq.share_token}`;
    navigator.clipboard.writeText(url); toast.success(t("common.linkCopied"));
  }
  function shareTplWhatsApp(rq) {
    const url = `${window.location.origin}/consentimiento-pdf/${rq.share_token}`;
    openWhatsAppTo(`${patient.full_name} — *${rq.treatment || ""}*: ${url}`);
  }
  function shareTplEmail(rq) {
    const url = `${window.location.origin}/consentimiento-pdf/${rq.share_token}`;
    openEmailTo(`${t("patientDetail.consentEmailSubject")} — ${rq.treatment || ""}`, `${t("patientDetail.consentEmailBody")}\n\n${url}`);
  }
  async function delTplReq(id) {
    if (!window.confirm(t("patientDetail.confirmDeleteConsent"))) return;
    await api.delete(`/consent-template-requests/${id}`); load();
  }
  function downloadSignedTpl(rq) {
    const token = localStorage.getItem("token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/consent-template-requests/${rq.id}/signed?auth=${token}`, "_blank");
  }

  const uploadRef = useRef();
  const [uploadTreatment, setUploadTreatment] = useState("");
  const [uploading, setUploading] = useState(false);
  async function uploadPaper(e) {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const q = uploadTreatment ? `?treatment=${encodeURIComponent(uploadTreatment)}` : "";
      await api.post(`/patients/${pid}/consents/upload${q}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("patientDetail.paperConsentUploaded") || "Consenso caricato");
      setUploadTreatment(""); e.target.value = ""; load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  }

  async function generateRemoteLink() {
    if (!treatment.trim()) { toast.error(t("patientDetail.indicateTreatment")); return; }
    try {
      const { data } = await api.post(`/patients/${pid}/consent-requests`, { treatment, body_text: body });
      const url = `${window.location.origin}${data.url_path}`;
      setRemoteLink(url);
      toast.success(t("patientDetail.linkGenerated"));
    } catch (e) { toast.error(apiError(e)); }
  }

  async function copyLink() {
    await navigator.clipboard.writeText(remoteLink);
    toast.success(t("common.linkCopied"));
  }

  function shareByWhatsApp() {
    openWhatsAppTo(`${patient.full_name} — *${treatment}*: ${remoteLink}`);
  }

  function shareByEmail() {
    openEmailTo(`${t("patientDetail.consentEmailSubject")} — ${treatment}`, `${t("patientDetail.consentEmailBody")}\n\n${remoteLink}`);
  }

  async function sign() {
    if (!treatment.trim()) { toast.error(t("patientDetail.indicateTreatment")); return; }
    if (sigRef.current.isEmpty()) { toast.error(t("patientDetail.addSignature")); return; }
    const dataUrl = sigRef.current.getTrimmedCanvas().toDataURL("image/png");
    try {
      await api.post(`/patients/${pid}/consents`, { treatment, body_text: body, signature_data_url: dataUrl });
      toast.success(t("patientDetail.consentSaved"));
      setTreatment(""); setBody(DEFAULT_CONSENT); sigRef.current.clear(); load();
    } catch (e) { toast.error(apiError(e)); }
  }

  async function downloadPDF(c) {
    const doc = new jsPDF();
    doc.setFontSize(18); doc.text("CONSENTIMIENTO INFORMADO", 105, 20, { align: "center" });
    doc.setFontSize(11);
    doc.text(`Paciente: ${patient.full_name}`, 20, 40);
    doc.text(`Tel: ${patient.phone}`, 20, 48);
    doc.text(`${c.treatment}`, 20, 56);
    doc.text(`${new Date(c.signed_at).toLocaleDateString(locale)}`, 20, 64);
    doc.line(20, 68, 190, 68);
    const lines = doc.splitTextToSize(c.body_text, 170);
    doc.text(lines, 20, 78);
    const token = localStorage.getItem("token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consents/${c.id}/signature?auth=${token}`);
    const blob = await res.blob();
    const reader = new FileReader();
    reader.onload = () => {
      doc.text("Firma:", 20, 240);
      doc.addImage(reader.result, "PNG", 20, 245, 60, 30);
      doc.line(20, 280, 100, 280);
      doc.setFontSize(9);
      doc.text(patient.full_name, 20, 286);
      doc.save(`consentimiento-${patient.full_name.replace(/\s+/g, "_")}-${c.treatment.replace(/\s+/g, "_")}.pdf`);
    };
    reader.readAsDataURL(blob);
  }

  async function del(id) {
    if (!window.confirm(t("patientDetail.confirmDeleteConsent"))) return;
    await api.delete(`/consents/${id}`); load();
  }

  return (
    <div className="space-y-8">
      {/* Send PDF template block */}
      <div className="bg-white border border-stone-200 rounded-2xl p-8 space-y-5" data-testid="tpl-consent-block">
        <div>
          <h3 className="font-heading text-2xl font-light">{t("patientDetail.sendPdfConsent")}</h3>
          <p className="text-sm text-stone-500 mt-1">{t("patientDetail.sendPdfConsentDesc")}</p>
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-stone-500">
            <span>📱 {patient.phone || <em className="text-red-500">{t("patientDetail.missingPhone")}</em>}</span>
            <span>✉️ {patient.email || <em className="text-red-500">{t("patientDetail.missingEmail")}</em>}</span>
            <Link to={`#`} onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="text-[#B07D4F] underline underline-offset-4">{t("patientDetail.editContact")}</Link>
          </div>
        </div>
        {templates.length === 0 ? (
          <div className="flex items-center gap-3 bg-[#FAF9F6] border border-dashed border-stone-300 rounded-xl px-4 py-3 text-sm text-stone-600">
            <FileText className="w-4 h-4 text-stone-400" />
            <span>{t("patientDetail.noTemplatesUploaded")} <Link to="/plantillas" className="text-[#B07D4F] underline underline-offset-4">{t("nav.templates")}</Link></span>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("patientDetail.selectTemplate")} *</label>
                <select value={selectedTpl} onChange={(e) => setSelectedTpl(e.target.value)} data-testid="tpl-select"
                  className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl">
                  <option value="">—</option>
                  {templates.map((tp) => <option key={tp.id} value={tp.id}>{tp.treatment || tp.original_filename}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.notes")}</label>
                <input value={tplNotes} onChange={(e) => setTplNotes(e.target.value)} data-testid="tpl-notes"
                  className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => sendTemplate("whatsapp")} data-testid="tpl-send-btn"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white transition-colors">
                <Send className="w-4 h-4" /> {t("patientDetail.generateAndSendWhatsapp")}
              </button>
              <button onClick={() => sendTemplate("email")} data-testid="tpl-send-email-btn"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-[#D4A373] text-[#B07D4F] hover:bg-[#D4A373] hover:text-stone-900 transition-colors">
                <Mail className="w-4 h-4" /> {t("patientDetail.generateAndSendEmail")}
              </button>
            </div>
          </>
        )}

        {tplRequests.length > 0 && (
          <div className="pt-4 border-t border-stone-100">
            <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-3">{t("patientDetail.sentConsents")}</p>
            <div className="space-y-3">
              {tplRequests.map((rq) => (
                <div key={rq.id} data-testid={`tpl-req-${rq.id}`} className="p-4 border border-stone-100 rounded-xl bg-[#FAF9F6] flex items-center justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-stone-900">{rq.treatment || rq.template_filename}</span>
                      {rq.status === "signed" ? (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#8FA08B] text-white">{t("patientDetail.statusSigned")}</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-stone-200 text-stone-700">{t("patientDetail.statusPending")}</span>
                      )}
                    </div>
                    <div className="text-xs text-stone-500 mt-1">{new Date(rq.created_at).toLocaleString(locale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                    {rq.signed_at && <div className="text-xs text-[#8FA08B] mt-0.5">{t("patientDetail.signedOn")} {new Date(rq.signed_at).toLocaleString(locale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {rq.status === "signed" && (
                      <button onClick={() => downloadSignedTpl(rq)} data-testid={`tpl-download-signed-${rq.id}`}
                        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white">
                        <Download className="w-3 h-3" /> PDF
                      </button>
                    )}
                    <button onClick={() => shareTplWhatsApp(rq)} data-testid={`tpl-share-${rq.id}`}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[#8FA08B] text-white hover:opacity-90">
                      <Send className="w-3 h-3" /> WhatsApp
                    </button>
                    <button onClick={() => shareTplEmail(rq)} data-testid={`tpl-share-email-${rq.id}`}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-stone-700">
                      <Mail className="w-3 h-3" /> Email
                    </button>
                    <button onClick={() => copyTplLink(rq)} className="text-stone-400 hover:text-stone-900"><Copy className="w-4 h-4" /></button>
                    <button onClick={() => delTplReq(rq.id)} className="text-stone-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <h3 className="font-heading text-2xl font-light">{t("patientDetail.signedConsents")}</h3>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">{t("patientDetail.treatment")}</label>
              <input value={uploadTreatment} onChange={(e) => setUploadTreatment(e.target.value)} data-testid="upload-consent-treatment"
                placeholder={t("patientDetail.consentTreatmentPh")}
                className="px-3 py-2 bg-[#FAF9F6] border border-stone-200 rounded-lg text-sm w-52" />
            </div>
            <input ref={uploadRef} type="file" accept="application/pdf,image/*" capture="environment" multiple
              onChange={uploadPaper} className="hidden" data-testid="upload-consent-input" />
            <button onClick={() => uploadRef.current?.click()} disabled={uploading} data-testid="upload-consent-btn"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white transition-colors disabled:opacity-50">
              <Upload className="w-4 h-4" /> {uploading ? "…" : (t("patientDetail.uploadPaperConsent") || "Caricare")}
            </button>
          </div>
        </div>
        <p className="text-xs text-stone-500 mb-4 -mt-2">{t("patientDetail.uploadPaperHint") || "Puoi caricare un PDF o più foto (verranno combinate in un unico documento)."}</p>
        {consents.length === 0 ? (
          <p className="text-stone-500 text-sm">{t("patientDetail.noConsents")}</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-4 stagger">
            {consents.map((c) => (
              <div key={c.id} data-testid={`consent-${c.id}`} className="bg-white border border-stone-200 rounded-2xl p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-medium">{c.treatment}</div>
                    <div className="text-xs text-stone-500 mt-1">{t("patientDetail.signedOn")} {new Date(c.signed_at).toLocaleDateString(locale)}</div>
                    {c.source === "manual_upload" && (
                      <span className="inline-block mt-1.5 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#F5EFE6] text-[#B07D4F] border border-[#D4A373]/40">
                        {t("patientDetail.paperConsentBadge") || "Cartaceo"}{c.pages_count > 1 ? ` · ${c.pages_count} pagine` : ""}
                      </span>
                    )}
                  </div>
                  <FileText className="w-4 h-4 text-[#D4A373]" strokeWidth={1.5} />
                </div>
                <div className="flex gap-2 mt-4">
                  {c.source === "manual_upload" ? (
                    <button onClick={() => {
                      const token = localStorage.getItem("token");
                      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/consents/${c.id}/uploaded?auth=${token}`, "_blank");
                    }} data-testid={`open-uploaded-consent-${c.id}`}
                      className="flex items-center gap-1 px-4 py-2 rounded-full bg-[#D4A373] text-stone-900 text-xs hover:bg-[#B07D4F] hover:text-white transition-colors">
                      <ExternalLink className="w-3.5 h-3.5" /> {t("common.open") || "Apri"}
                    </button>
                  ) : (
                    <button onClick={() => downloadPDF(c)} data-testid={`download-consent-${c.id}`}
                      className="flex items-center gap-1 px-4 py-2 rounded-full bg-[#D4A373] text-stone-900 text-xs hover:bg-[#B07D4F] hover:text-white transition-colors">
                      <Download className="w-3.5 h-3.5" /> PDF
                    </button>
                  )}
                  <button onClick={() => del(c.id)} className="px-4 py-2 rounded-full border border-stone-200 text-xs hover:bg-stone-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function InvoicesTab({ pid, patient, locale }) {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [items, setItems] = useState([{ description: "", quantity: 1, unit_price: 0 }]);
  const [tax, setTax] = useState(21);
  const [notes, setNotes] = useState("");

  const load = async () => { const { data } = await api.get(`/patients/${pid}/invoices`); setInvoices(data); };
  useEffect(() => { load(); }, [pid]);

  const subtotal = items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);
  const total = subtotal * (1 + tax / 100);

  async function create() {
    if (items.length === 0 || !items.some((i) => i.description)) { toast.error(t("patientDetail.addConcepts")); return; }
    try {
      await api.post(`/patients/${pid}/invoices`, {
        patient_id: pid,
        items: items.map((i) => ({ description: i.description, quantity: Number(i.quantity), unit_price: Number(i.unit_price) })),
        tax_percent: Number(tax), notes,
      });
      toast.success(t("patientDetail.invoiceCreated"));
      setItems([{ description: "", quantity: 1, unit_price: 0 }]); setNotes(""); load();
    } catch (e) { toast.error(apiError(e)); }
  }

  function downloadPDF(inv) {
    const doc = new jsPDF();
    doc.setFontSize(22); doc.text("FACTURA", 20, 25);
    doc.setFontSize(10); doc.text(`Nº: ${inv.number}`, 150, 25);
    doc.text(`${new Date(inv.created_at).toLocaleDateString(locale)}`, 150, 32);
    doc.setFontSize(11);
    doc.text("Cliente:", 20, 50);
    doc.text(patient.full_name, 20, 57);
    if (patient.email) doc.text(patient.email, 20, 63);
    if (patient.phone) doc.text(patient.phone, 20, 69);
    let y = 90;
    doc.setFontSize(9);
    doc.text("CONCEPTO", 20, y); doc.text("CANT.", 130, y); doc.text("PRECIO", 150, y); doc.text("TOTAL", 180, y);
    doc.line(20, y + 2, 195, y + 2);
    y += 10;
    inv.items.forEach((it) => {
      doc.text(it.description.substring(0, 60), 20, y);
      doc.text(String(it.quantity), 130, y);
      doc.text(`€${it.unit_price.toFixed(2)}`, 150, y);
      doc.text(`€${(it.quantity * it.unit_price).toFixed(2)}`, 180, y);
      y += 8;
    });
    y += 4;
    doc.line(130, y, 195, y); y += 6;
    doc.text(`Subtotal:`, 150, y); doc.text(`€${inv.subtotal.toFixed(2)}`, 180, y); y += 6;
    doc.text(`IVA (${inv.tax_percent}%):`, 150, y); doc.text(`€${inv.tax.toFixed(2)}`, 180, y); y += 6;
    doc.setFontSize(12);
    doc.text(`TOTAL:`, 150, y); doc.text(`€${inv.total.toFixed(2)}`, 180, y);
    if (inv.notes) { doc.setFontSize(9); doc.text(inv.notes, 20, y + 20); }
    doc.save(`factura-${inv.number}.pdf`);
  }

  return (
    <div className="space-y-8">
      <div className="bg-white border border-stone-200 rounded-2xl p-8 space-y-5">
        <h3 className="font-heading text-2xl font-light">{t("patientDetail.newInvoice")}</h3>
        <div className="space-y-3">
          {items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-end">
              <input placeholder={t("patientDetail.invoiceDesc")} value={it.description}
                onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))}
                data-testid={`invoice-desc-${idx}`}
                className="col-span-6 px-3 py-2 bg-[#FAF9F6] border border-stone-200 rounded-xl text-sm" />
              <input type="number" step="0.01" placeholder={t("patientDetail.invoiceQty")} value={it.quantity}
                onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, quantity: e.target.value } : x))}
                data-testid={`invoice-qty-${idx}`}
                className="col-span-2 px-3 py-2 bg-[#FAF9F6] border border-stone-200 rounded-xl text-sm" />
              <input type="number" step="0.01" placeholder="€" value={it.unit_price}
                onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, unit_price: e.target.value } : x))}
                data-testid={`invoice-price-${idx}`}
                className="col-span-3 px-3 py-2 bg-[#FAF9F6] border border-stone-200 rounded-xl text-sm" />
              <button onClick={() => setItems(items.filter((_, i) => i !== idx))} className="col-span-1 py-2 text-stone-400 hover:text-red-500">
                <Trash2 className="w-4 h-4 mx-auto" />
              </button>
            </div>
          ))}
        </div>
        <button onClick={() => setItems([...items, { description: "", quantity: 1, unit_price: 0 }])} data-testid="add-invoice-item"
          className="text-xs uppercase tracking-[0.2em] text-[#D4A373] hover:text-[#B07D4F]">+ {t("patientDetail.addInvoiceItem")}</button>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("patientDetail.vat")}</label>
            <input type="number" value={tax} onChange={(e) => setTax(e.target.value)} data-testid="invoice-tax"
              className="w-full px-4 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("common.notes")}</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full px-4 py-2.5 bg-[#FAF9F6] border border-stone-200 rounded-xl" />
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-stone-100">
          <div className="text-sm">
            <div className="text-stone-500">{t("patientDetail.subtotal")}: €{subtotal.toFixed(2)}</div>
            <div className="font-heading text-3xl font-light">€{total.toFixed(2)}</div>
          </div>
          <button onClick={create} data-testid="create-invoice"
            className="px-6 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800">{t("patientDetail.issueInvoice")}</button>
        </div>
      </div>

      <div>
        <h3 className="font-heading text-2xl font-light mb-4">{t("patientDetail.patientInvoices")}</h3>
        {invoices.length === 0 ? (
          <p className="text-stone-500 text-sm">{t("patientDetail.noInvoices")}</p>
        ) : (
          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="text-xs uppercase tracking-[0.15em] text-stone-500 border-b border-stone-100">
                  <th className="text-left px-6 py-4">{t("patientDetail.invoiceNumber")}</th>
                  <th className="text-left px-6 py-4">{t("common.date")}</th>
                  <th className="text-right px-6 py-4">{t("patientDetail.invoiceTotal")}</th>
                  <th className="px-6 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-b border-stone-50">
                    <td className="px-6 py-4">{inv.number}</td>
                    <td className="px-6 py-4 text-sm text-stone-600">{new Date(inv.created_at).toLocaleDateString(locale)}</td>
                    <td className="px-6 py-4 text-right">€{inv.total.toFixed(2)}</td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => downloadPDF(inv)} data-testid={`download-invoice-${inv.id}`}
                        className="text-xs uppercase tracking-[0.2em] text-[#D4A373] hover:text-[#B07D4F]">
                        <Download className="w-3.5 h-3.5 inline mr-1" /> PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
